import { appApi } from './app_api';

export const usersApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getUser: builder.query({
      query: (id) => `/users/${id}`,
      transformResponse: (response) => response.user,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    getDummyUser: builder.query({
      query: (dummyUserId) => `/dummy_users/${dummyUserId}`,
      transformResponse: (response) => response.user,
      providesTags: (result, error, dummyUserId) => [{ type: 'DummyUser', id: dummyUserId }],
    }),
    getNextEvents: builder.query({
      query: (id) => `/users/${id}/next_events`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: 'UserNextEvents', id }],
    }),
    getDummyNextEvents: builder.query({
      query: (dummyUserId) => `/dummy_users/${dummyUserId}/next_events`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, dummyUserId) => [{ type: 'DummyUserNextEvents', id: dummyUserId }],
    }),
    getUserStats: builder.query({
      query: ({ userId, customerId }) => `/users/${userId}/stats?customer_id=${customerId}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: 'UserStats', id }],
    }),
    getDummyUserStats: builder.query({
      query: ({ dummyUserId, customerId }) => `/dummy_users/${dummyUserId}/stats?customer_id=${customerId}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: 'DummyUserStats', id }],
    }),
    getPossibleCustomers: builder.query({
      query: (id) => `/users/${id}/possible_customers`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, id) => [{ type: 'UserPossibleCustomers', id }],
    }),
    getDummyPossibleCustomers: builder.query({
      query: (dummyUserId) => `/dummy_users/${dummyUserId}/possible_customers`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, dummyUserId) => [{ type: 'DummyUserPossibleCustomers', id: dummyUserId }],
    }),
    getMatchesForUser: builder.query({
      query: ({ userId, eventId }) => `/users/${userId}/matches?event_id=${eventId}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, { userId, eventId }) => [{ type: 'UserMatches', id: `${userId}-${eventId}` }],
    }),
    getDummyMatchesForUser: builder.query({
      query: ({ dummyUserId, eventId }) => `/dummy_users/${dummyUserId}/matches?event_id=${eventId}`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, { dummyUserId, eventId }) => [{ type: 'DummyUserMatches', id: `${dummyUserId}-${eventId}` }],
    }),
    getMedalsForUser: builder.query({
      query: (userId) => `/users/${userId}/medals`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, userId) => [{ type: 'UserMedals', id: userId }],
    }),
    getUserPhoneNumber: builder.query({
      query: (userId) => `/users/${userId}/phone_number`,
      transformResponse: (response) => response.phone,
    }),
    getDummyMedalsForUser: builder.query({
      query: (dummyUserId) => `/dummy_users/${dummyUserId}/medals`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, dummyUserId) => [{ type: 'DummyUserMedals', id: dummyUserId }],
    }),
    editUser: builder.mutation({
      query: ({ userId, ...patch }) => ({
        url: `/users/${userId}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: 'User', id: userId }],
    }),
    sendRecoveryCode: builder.mutation({
      query: (phone) => ({
        url: '/users/send_recovery_code',
        method: 'POST',
        body: { phone },
      }),
    }),
    verifyRecoveryCode: builder.mutation({
      query: ({ phone, recovery_code }) => ({
        url: '/users/verify_recovery_code',
        method: 'POST',
        body: { phone, recovery_code },
      }),
    }),
    changePassword: builder.mutation({
      query: ({ new_password, confirm_password, token }) => ({
        url: '/users/change_password',
        method: 'POST',
        body: { new_password, confirm_password, token },
      }),
    }),
    registerUser: builder.mutation({
      query: ({ email, firstName, lastName, phone, password, city, acceptedTerms }) => ({
        url: '/users',
        method: 'POST',
        body: { user: { email, first_name: firstName, last_name: lastName, phone, password, city, accepted_terms: acceptedTerms, accepted_privacy_policy: acceptedTerms } },
      }),
      transformResponse: (response) => response.user,
    }),
    verifyPhone: builder.mutation({
      query: ({ userId, code }) => ({
        url: `/users/${userId}/verify_user`,
        method: 'POST',
        body: { verification_code: code },
      }),
      transformResponse: (response) => response.user,
    }),
    resendVerification: builder.mutation({
      query: (userId) => ({
        url: `/users/${userId}/resend_verification_code`,
        method: 'POST',
      }),
    }),
    presignProfilePicture: builder.mutation({
      query: ({ userId, fileName, contentType, byteSize }) => ({
        url: `/users/${userId}/presign_profile_picture`,
        method: 'POST',
        body: {
          file_name: fileName,
          content_type: contentType,
          byte_size: byteSize,
        },
      }),
      transformResponse: (response) => response.data,
    }),
    uploadProfilePictureToPresignedUrl: builder.mutation({
      async queryFn({ uploadUrl, headers, file }) {
        try {
          const response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: headers || { 'Content-Type': file.type },
            body: file,
          });
          if (!response.ok) {
            return { error: { status: response.status, data: 'Upload failed' } };
          }
          return { data: null };
        } catch (e) {
          return { error: { status: 'FETCH_ERROR', data: e.message } };
        }
      },
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetDummyUserQuery,
  useGetNextEventsQuery,
  useGetDummyNextEventsQuery,
  useGetPossibleCustomersQuery,
  useGetDummyPossibleCustomersQuery,
  useGetUserStatsQuery,
  useGetDummyUserStatsQuery,
  useGetMatchesForUserQuery,
  useGetDummyMatchesForUserQuery,
  useGetMedalsForUserQuery,
  useGetDummyMedalsForUserQuery,
  useLazyGetUserPhoneNumberQuery,
  useEditUserMutation,
  useSendRecoveryCodeMutation,
  useVerifyRecoveryCodeMutation,
  useChangePasswordMutation,
  usePresignProfilePictureMutation,
  useUploadProfilePictureToPresignedUrlMutation,
  useRegisterUserMutation,
  useVerifyPhoneMutation,
  useResendVerificationMutation,
} = usersApi;
